import React, { useCallback, useEffect, useState, useRef } from 'react';
import VaultSidebar from '../../components/vault/VaultSidebar';
import VaultHeader from '../../components/vault/VaultHeader';
import { useCategories } from '../../contexts/CategoryContext';
import { useVault } from '../../contexts/VaultContext';
import CategoryModal from '../../components/vault/CategoryModal';
import { ICategory } from '../../types/category.types';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, ChevronDownIcon, ChevronRightIcon, KeyIcon, DocumentTextIcon, CreditCardIcon, BuildingLibraryIcon, UserIcon, BriefcaseIcon, ArrowLeftIcon, ShareIcon, EllipsisVerticalIcon, AdjustmentsHorizontalIcon, ArrowsUpDownIcon, XMarkIcon, ArrowUpIcon, ArrowDownIcon, FunnelIcon, UsersIcon, UserGroupIcon, MagnifyingGlassIcon, EyeIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import PasswordList from '../../components/vault/PasswordList';
import { IDecryptedPasswordEntry } from '../../types/vault.types';
import QuickAddItem from '../../components/vault/QuickAddItem';
import { VaultService } from '../../services/vault.service';
import { CategoryService, ICategoryBasic } from '../../services/category.service';
import NotesList from '../../components/notes/NotesList';
import CardDetailsList from '../../components/cardDetails/CardDetailsList';
import BankDetailsList from '../../components/bankDetails/BankDetailsList';
import PersonalDetailsList from '../../components/personalDetails/PersonalDetailsList';
import { Note } from '../../types/notes';
import { CardDetail } from '../../types/cardDetails';
import { BankDetail } from '../../types/bankDetails';
import { PersonalDetail } from '../../types/personalDetails';
import { secureStore } from '../../utils/secureStore';
import { decryptData, decryptKeyData } from '../../utils/encryption';
import CreateNoteModal from '../../components/notes/CreateNoteModal';
import CardDetailsModal from '../../components/cardDetails/CardDetailsModal';
import BankDetailsModal from '../../components/bankDetails/BankDetailsModal';
import PersonalDetailsModal from '../../components/personalDetails/PersonalDetailsModal';
import DeleteCategoryModal from '../../components/vault/DeleteCategoryModal';
import ShareVaultModal from '../../components/vault/ShareVaultModal';
import { useAppSelector } from '../../hooks/useRedux';
import AddItemModal from '../../components/vault/AddItemModal';
import PasswordModal from '../../components/vault/PasswordModal';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import ConfirmModal from '../../components/common/ConfirmModal';
import { ShareService } from '../../services/share.service';
import { hasPermission } from '../../utils/permissionUtils';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';

interface CategoryWithEntries {
    category_id: string;
    category_name: string;
    category_description: string;
    category_color: string;
    category_icon: string | null;
    id: string;
    name: string;
    description: string;
    color: string;
    icon: string | null;
    vault_id: string;
    created_at: Date;
    updated_at: Date;
    passwords?: IDecryptedPasswordEntry[];
    notes?: Note[];
    cards?: CardDetail[];
    bank_details?: BankDetail[];
    personal_details?: PersonalDetail[];
    is_shared?: boolean;
    shared_by_email?: string;
    permission_level?: string;
    expires_at?: Date;
    shared_key?: string;
    shared_with_users?: { id: string; email: string; permission_level: string }[];
    shared_with_teams?: { id: string; name: string; permission_level: string }[];
}

// Define navigation levels and item types
type NavigationLevel = 'root' | 'category';
type TabType = 'passwords' | 'notes' | 'cardDetails' | 'bankDetails' | 'personalDetails';
type SortOption = 'newest' | 'alphabetical' | 'passwordStrength';
type PasswordStrengthFilter = 'all' | 'veryWeak' | 'weak' | 'fair' | 'strong' | 'veryStrong';

interface SharedNote extends Note {
    isSharedNote?: boolean;
    sharedKey?: string | null;
}

interface SharedCardDetail extends CardDetail {
    isSharedCard?: boolean;
    sharedKey?: string | null;
}

interface SharedBankDetail extends BankDetail {
    isSharedBank?: boolean;
    sharedKey?: string | null;
}

interface SharedPersonalDetail extends PersonalDetail {
    isSharedPersonal?: boolean;
    sharedKey?: string | null;
}

// Add the interface for category info
interface SelectedCategoryInfo {
    id: string;
    isShared: boolean;
    sharedKey?: string;
    permissionLevel?: string;
}

const CategoriesPage: React.FC = () => {
    const { createCategory, updateCategory, deleteCategory, getCategoryEntries } = useCategories();
    const { searchQuery } = useVault();
    const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [categoriesWithEntries, setCategoriesWithEntries] = useState<CategoryWithEntries[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
    const [selectedNote, setSelectedNote] = useState<SharedNote | null>(null);
    const [isCardModalOpen, setIsCardModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState<SharedCardDetail | null>(null);
    const [isBankModalOpen, setIsBankModalOpen] = useState(false);
    const [selectedBank, setSelectedBank] = useState<SharedBankDetail | null>(null);
    const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
    const [selectedPersonal, setSelectedPersonal] = useState<SharedPersonalDetail | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState<{ id: string, name: string } | null>(null);
    const [selectedSharedKey, setSelectedSharedKey] = useState<string>('');
    const [permissionLevel, setPermissionLevel] = useState<string>('');

    // Navigation state
    const [currentLevel, setCurrentLevel] = useState<NavigationLevel>('root');
    const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
    const [activeCategory, setActiveCategory] = useState<CategoryWithEntries | null>(null);
    const [loading, setLoading] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [vaultToShare, setVaultToShare] = useState<{ id: string, name: string } | null>(null);
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
    const { role_id, team_id, permissions } = useAppSelector(state => state.auth);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const [allCategories, setAllCategories] = useState<ICategory[]>([]);
    useOnClickOutside(dropdownRef, () => {
        if (openDropdownId !== null) {
            setOpenDropdownId(null);
        }
    });

    // Tab related states
    const [activeTab, setActiveTab] = useState<TabType>('passwords');
    const [isItemModalOpen, setIsItemModalOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

    // Debug logging for modal state
    useEffect(() => {
        console.log('Modal state changed:', { isItemModalOpen, activeCategoryId, activeCategory: !!activeCategory });
    }, [isItemModalOpen, activeCategoryId, activeCategory]);

    // Loading states for tabs
    const [loadingPasswords, setLoadingPasswords] = useState<boolean>(false);
    const [loadingNotes, setLoadingNotes] = useState<boolean>(false);
    const [loadingCards, setLoadingCards] = useState<boolean>(false);
    const [loadingBanks, setLoadingBanks] = useState<boolean>(false);
    const [loadingPersonals, setLoadingPersonals] = useState<boolean>(false);

    // Sorting and filtering states
    const [sortOption, setSortOption] = useState<SortOption>('newest');
    const [passwordStrengthFilter, setPasswordStrengthFilter] = useState<PasswordStrengthFilter>('all');
    const [showSharedOnly, setShowSharedOnly] = useState<boolean>(false);
    const [tempPasswordStrengthFilter, setTempPasswordStrengthFilter] = useState<PasswordStrengthFilter>('all');
    const [tempShowSharedOnly, setTempShowSharedOnly] = useState<boolean>(false);
    const [isSortMenuOpen, setIsSortMenuOpen] = useState<boolean>(false);
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState<boolean>(false);
    const sortMenuRef = useRef<HTMLDivElement>(null);
    const filterMenuRef = useRef<HTMLDivElement>(null);

    const { activeWorkspace } = useWorkspace();

    const [showSharingDetails, setShowSharingDetails] = useState(false);
    const [showSharingDetailsModal, setShowSharingDetailsModal] = useState(false);

    // Add new state variables for confirmation modal
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalType, setConfirmModalType] = useState<'revoke' | 'permission'>('revoke');
    const [currentShareId, setCurrentShareId] = useState<string>('');
    const [currentSharedEntity, setCurrentSharedEntity] = useState<{ id: string, name: string, email?: string }>({ id: '', name: '' });
    const [newPermission, setNewPermission] = useState<string>('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [currentShareType, setCurrentShareType] = useState<'user' | 'team' | ''>('');
    const [sharingSearchQuery, setSharingSearchQuery] = useState<string>('');

    // Creation modal states for QuickAdd
    const [quickAddPasswordModalOpen, setQuickAddPasswordModalOpen] = useState(false);
    const [quickAddCardModalOpen, setQuickAddCardModalOpen] = useState(false);
    const [quickAddBankModalOpen, setQuickAddBankModalOpen] = useState(false);
    const [quickAddPersonalModalOpen, setQuickAddPersonalModalOpen] = useState(false);
    const [selectedCategoryForModal, setSelectedCategoryForModal] = useState<string | null>(null);
    const [selectedCategoryInfo, setSelectedCategoryInfo] = useState<SelectedCategoryInfo | null>(null);

    type SharedEntity = { id: string; permission_level: string; [key: string]: any };

    const updateShareCollections = useCallback(
        (type: 'user' | 'team', transform: (list: SharedEntity[]) => SharedEntity[]) => {
            if (!activeCategoryId) return;

            const key: 'shared_with_users' | 'shared_with_teams' =
                type === 'user' ? 'shared_with_users' : 'shared_with_teams';

            setActiveCategory(prev => {
                if (!prev) return prev;
                const currentList = Array.isArray(prev[key]) ? [...(prev[key] as SharedEntity[])] : [];
                const updatedList = transform(currentList);
                return {
                    ...prev,
                    [key]: updatedList
                } as CategoryWithEntries;
            });

            setAllCategories(prev =>
                prev.map(category => {
                    if (!category || category.id !== activeCategoryId) return category;
                    const currentList = Array.isArray((category as any)[key])
                        ? [...((category as any)[key] as SharedEntity[])]
                        : [];
                    const updatedList = transform(currentList);
                    return {
                        ...category,
                        [key]: updatedList
                    } as ICategory;
                })
            );
        },
        [activeCategoryId]
    );

    const applyLocalPermissionUpdate = useCallback(
        (type: 'user' | 'team', entityId: string, permission: string) => {
            updateShareCollections(type, list =>
                list.map(entity =>
                    entity.id === entityId
                        ? {
                            ...entity,
                            permission_level: permission
                        }
                        : entity
                )
            );
        },
        [updateShareCollections]
    );

    const removeLocalShareEntity = useCallback(
        (type: 'user' | 'team', entityId: string) => {
            updateShareCollections(type, list => list.filter(entity => entity.id !== entityId));
        },
        [updateShareCollections]
    );

    const getAllCategoriesBasic = useCallback(async (): Promise<ICategoryBasic[]> => {
        const data = await CategoryService.getAllCategoriesBasic(activeWorkspace?.id || team_id || undefined);
        setAllCategories(data);
        return data;
    }, [activeWorkspace?.id, team_id]);

    const handleShareSuccess = useCallback(async () => {
        if (!activeCategoryId) {
            await getAllCategoriesBasic();
            return;
        }

        const updatedCategories = await getAllCategoriesBasic();
        const updatedCategory = updatedCategories.find(category => category.id === activeCategoryId);

        if (updatedCategory) {
            setActiveCategory(prev => {
                if (!prev) return prev;
                return {
                    ...prev,
                    shared_with_users: updatedCategory.shared_with_users || [],
                    shared_with_teams: updatedCategory.shared_with_teams || []
                } as CategoryWithEntries;
            });
        }
    }, [activeCategoryId, getAllCategoriesBasic]);

    const handleCreateCategory = async (data: { name: string; description?: string; color?: string; workspaceId?: string }) => {
        try {
            await createCategory(data);
            toast.success('Vault created successfully');
            setIsCreateModalOpen(false);
            getAllCategoriesBasic();
        } catch (error) {
            toast.error('Failed to create vault');
        }
    };

    const handleUpdateCategory = async (data: { name: string; description?: string; color?: string }) => {
        if (!selectedCategory) return;
        try {
            await updateCategory(selectedCategory.id, data);
            toast.success('Vault updated successfully');
            getAllCategoriesBasic();
            setIsEditModalOpen(false);
            setSelectedCategory(null);
        } catch (error) {
            toast.error('Failed to update vault');
        }
    };

    const handleDeleteCategoryClick = (categoryId: string, categoryName: string) => {
        setCategoryToDelete({ id: categoryId, name: categoryName });
        setIsDeleteModalOpen(true);
    };

    // Navigation handlers
    const handleCategoryClick = async (category: ICategory) => {
        // For unassigned category, category object exists but category_id is null
        // Safely access category_id with optional chaining
        const categoryId = category?.id;

        const item = await CategoryService.getCategoryItems(categoryId, activeWorkspace?.id || undefined);
        const decryptedItem = {
            ...item,
            is_shared: category?.is_shared || false,
            shared_with_teams: category?.shared_with_teams || [],
            shared_with_users: category?.shared_with_users || [],
            passwords: Array.isArray(item.passwords) ? item.passwords.map((password: IDecryptedPasswordEntry) => decryptPasswordDetails(password, item.shared_key)) : [],
            cards: Array.isArray(item.cards) ? item.cards.map((card: CardDetail) => decryptCardDetails(card, item.shared_key, item.permission_level)) : [],
            bank_details: Array.isArray(item.bank_details) ? item.bank_details.map((bank: BankDetail) => decryptBankDetails(bank, item.shared_key, item.permission_level)) : [],
            personal_details: Array.isArray(item.personal_details) ? item.personal_details.map((personal: PersonalDetail) => decryptPersonalDetails(personal, item.shared_key, item.permission_level)) : []
        };

        // Get shared key if available
        let sharedKey = null;
        if (category?.shared_key && typeof category.shared_key === 'string' && category.shared_key.length > 0) {
            try {
                sharedKey = await decryptKeyData(category.shared_key);
            } catch (error) {
                console.error('Failed to decrypt shared_key:', error);
                sharedKey = null;
            }
        }

        // Set the active category info
        setActiveCategoryId(categoryId);
        setActiveCategory(decryptedItem as unknown as CategoryWithEntries);
        setCurrentLevel('category');
        setSelectedSharedKey(sharedKey || '');
        setPermissionLevel(category?.permission_level || '');

        // Set active tab to passwords by default
        setActiveTab('passwords');
    };

    const refreshCurrentCategory = async () => {
        if (currentLevel !== 'category') return;

        const categoryId = activeCategoryId ?? null;

        try {
            const item = await CategoryService.getCategoryItems(categoryId, activeWorkspace?.id || undefined);
            const decryptedItem = {
                ...item,
                is_shared: activeCategory?.is_shared || false,
                shared_with_teams: activeCategory?.shared_with_teams || [],
                shared_with_users: activeCategory?.shared_with_users || [],
                passwords: Array.isArray(item.passwords) ? item.passwords.map((password: IDecryptedPasswordEntry) => decryptPasswordDetails(password, item.shared_key)) : [],
                cards: Array.isArray(item.cards) ? item.cards.map((card: CardDetail) => decryptCardDetails(card, item.shared_key, item.permission_level)) : [],
                bank_details: Array.isArray(item.bank_details) ? item.bank_details.map((bank: BankDetail) => decryptBankDetails(bank, item.shared_key, item.permission_level)) : [],
                personal_details: Array.isArray(item.personal_details) ? item.personal_details.map((personal: PersonalDetail) => decryptPersonalDetails(personal, item.shared_key, item.permission_level)) : []
            };
            setActiveCategory(decryptedItem as unknown as CategoryWithEntries);
        } catch (error) {
            console.error('Failed to refresh current vault', error);
        }
    };

    const handleBackClick = useCallback(() => {
        setCurrentLevel('root');
        setActiveCategoryId(null);
        setActiveCategory(null);
        setSelectedSharedKey('');
        setPermissionLevel('');
        setActiveTab('passwords');
        setIsShareModalOpen(false);
        setIsItemModalOpen(false);
        setIsPasswordModalOpen(false);
        setIsNoteModalOpen(false);
        setIsCardModalOpen(false);
        setIsBankModalOpen(false);
        setIsPersonalModalOpen(false);
        setOpenDropdownId(null);
        setSelectedCategoryForModal(null);
        setSelectedCategoryInfo(null);
        getAllCategoriesBasic();
    }, [getAllCategoriesBasic]);

    // Handle tab change
    const handleTabChange = (tab: TabType) => {
        setActiveTab(tab);
    };

    // Modal handlers
    const handleNoteModalClose = () => {
        setIsNoteModalOpen(false);
        setSelectedNote(null);
    };

    const handleCardModalClose = () => {
        setIsCardModalOpen(false);
        setSelectedCard(null);
    };

    const handleBankModalClose = () => {
        setIsBankModalOpen(false);
        setSelectedBank(null);
    };

    const handlePersonalModalClose = () => {
        setIsPersonalModalOpen(false);
        setSelectedPersonal(null);
    };

    const handleItemSelect = (itemType: string) => {
        if (itemType === 'password') {
            setQuickAddPasswordModalOpen(true);
        } else if (itemType === 'personalDetails') {
            setQuickAddPersonalModalOpen(true);
        } else if (itemType === 'cardDetails') {
            setQuickAddCardModalOpen(true);
        } else if (itemType === 'bankDetails') {
            setQuickAddBankModalOpen(true);
        }
        setIsItemModalOpen(false);
    };

    const handleSuccess = async () => {
        setIsPasswordModalOpen(false);
        refreshCurrentCategory();
        toast.success('Item added successfully');
    };

    // Decryption functions
    const decryptBufferData = (encryptedBuffer: any): string => {
        if (!encryptedBuffer) return '';

        try {
            if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
                const uint8Array = new Uint8Array(encryptedBuffer.data);
                return new TextDecoder().decode(uint8Array);
            }

            if (typeof encryptedBuffer === 'string') {
                return encryptedBuffer;
            }

            return '';
        } catch (error) {
            console.error('Error decrypting buffer:', error);
            return '';
        }
    };

    const decryptCardDetails = (card: any, sharedKey: string | null = null, permissionLevel: string | null = null) => {
        let vaultKey = '';
        
        // V2 decryption: Check if entry has vault_id and crypto_version = 2
        if (card?.crypto_version === 2 && card?.vault_id) {
            const vaultId = String(card.vault_id);
            if (secureStore.hasVaultLevelKey(vaultId)) {
                vaultKey = secureStore.getVaultLevelKey(vaultId) || '';
                console.log(`Using V2 vault key for card ${card.id} in vault ${vaultId}`);
            } else {
                console.warn(`V2 vault key not found for vault ${vaultId}`);
                // Fallback to V1 method
                vaultKey = card?.shared_key ? decryptKeyData(card?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
            }
        } else {
            // V1 decryption: Use shared_key or personal vault key
            vaultKey = card?.shared_key ? decryptKeyData(card?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
        }
        
        try {
            return {
                ...card,
                encrypted_card_holder_name: card.encrypted_card_holder_name
                    ? decryptData(decryptBufferData(card.encrypted_card_holder_name), vaultKey)
                    : '',
                encrypted_card_number: card.encrypted_card_number
                    ? decryptData(decryptBufferData(card.encrypted_card_number), vaultKey)
                    : '',
                encrypted_expiration_date: card.encrypted_expiration_date
                    ? decryptData(decryptBufferData(card.encrypted_expiration_date), vaultKey)
                    : '',
                cvv: card.encrypted_cvv
                    ? decryptData(decryptBufferData(card.encrypted_cvv), vaultKey)
                    : '',
                is_shared: sharedKey ? true : false,
                shared_key: sharedKey ? vaultKey : '',
                permission_level: sharedKey ? permissionLevel : '',
                itemType: sharedKey ? 'category' : ''
            };
        } catch (error) {
            console.error('Error decrypting card details:', error);
            return card;
        }
    };

    // Function to calculate password strength if not already available
    const calculatePasswordStrength = (password: string, username?: string): string => {
        let score = 0;

        // Handle null or undefined password
        if (!password) {
            return 'Very Weak';
        }

        // Length check
        if (password.length >= 12) {
            score += 20;
        }

        // Number check
        if (/\d/.test(password)) {
            score += 20;
        }

        // Lowercase check
        if (/[a-z]/.test(password)) {
            score += 20;
        }

        // Uppercase check
        if (/[A-Z]/.test(password)) {
            score += 20;
        }

        // Special character check
        if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            score += 20;
        }

        // Email check - if username is available and in the password
        if (username && password.toLowerCase().includes(username.toLowerCase())) {
            score -= 20;
        }

        // Get strength label
        let label = 'Very Weak';
        if (score <= 20) label = 'Very Weak';
        else if (score <= 40) label = 'Weak';
        else if (score <= 60) label = 'Fair';
        else if (score <= 80) label = 'Strong';
        else label = 'Very Strong';

        return label;
    };

    const decryptPasswordDetails = (password: any, sharedKey: string | null = null) => {
        let vaultKey = '';
        
        // V2 decryption: Check if entry has vault_id and crypto_version = 2
        if (password?.crypto_version === 2 && password?.vault_id) {
            const vaultId = String(password.vault_id);
            if (secureStore.hasVaultLevelKey(vaultId)) {
                vaultKey = secureStore.getVaultLevelKey(vaultId) || '';
                console.log(`Using V2 vault key for password ${password.id} in vault ${vaultId}`);
            } else {
                console.warn(`V2 vault key not found for vault ${vaultId}`);
                // Fallback to V1 method
                vaultKey = password?.shared_key ? decryptKeyData(password?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
            }
        } else {
            // V1 decryption: Use shared_key or personal vault key
            vaultKey = password?.shared_key ? decryptKeyData(password?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
        }
        
        try {
            const username = password.encrypted_username
                ? decryptData(decryptBufferData(password.encrypted_username), vaultKey)
                : '';

            const decryptedPassword = password.encrypted_password
                ? decryptData(decryptBufferData(password.encrypted_password), vaultKey)
                : '';

            // Determine password strength
            let passwordStrength = password.password_strength;
            if (!passwordStrength) {
                // Calculate strength if not stored in database
                passwordStrength = calculatePasswordStrength(decryptedPassword, username);
            }

            return {
                ...password,
                username,
                password: decryptedPassword,
                is_shared: sharedKey ? true : false,
                shared_key: sharedKey ? vaultKey : '',
                permission_level: sharedKey ? permissionLevel : '',
                itemType: sharedKey ? 'category' : '',
                password_strength: passwordStrength
            };
        } catch (error) {
            console.error('Error decrypting password details:', error);
            return password;
        }
    };

    const decryptBankDetails = (bank: any, sharedKey: string | null = null, permissionLevel: string | null = null) => {
        let vaultKey = '';
        
        // V2 decryption: Check if entry has vault_id and crypto_version = 2
        if (bank?.crypto_version === 2 && bank?.vault_id) {
            const vaultId = String(bank.vault_id);
            if (secureStore.hasVaultLevelKey(vaultId)) {
                vaultKey = secureStore.getVaultLevelKey(vaultId) || '';
                console.log(`Using V2 vault key for bank ${bank.id} in vault ${vaultId}`);
            } else {
                console.warn(`V2 vault key not found for vault ${vaultId}`);
                // Fallback to V1 method
                vaultKey = bank?.shared_key ? decryptKeyData(bank?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
            }
        } else {
            // V1 decryption: Use shared_key or personal vault key
            vaultKey = bank?.shared_key ? decryptKeyData(bank?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
        }
        
        try {
            return {
                ...bank,
                encrypted_account_number: bank.encrypted_account_number
                    ? decryptData(decryptBufferData(bank.encrypted_account_number), vaultKey)
                    : '',
                is_shared: sharedKey ? true : false,
                shared_key: sharedKey ? vaultKey : '',
                permission_level: sharedKey ? permissionLevel : '',
                itemType: sharedKey ? 'category' : ''
            };
        } catch (error) {
            console.error('Error decrypting bank details:', error);
            return bank;
        }
    };

    const decryptPersonalDetails = (personal: any, sharedKey: string | null = null, permissionLevel: string | null = null) => {
        let vaultKey = '';
        
        // V2 decryption: Check if entry has vault_id and crypto_version = 2
        if (personal?.crypto_version === 2 && personal?.vault_id) {
            const vaultId = String(personal.vault_id);
            if (secureStore.hasVaultLevelKey(vaultId)) {
                vaultKey = secureStore.getVaultLevelKey(vaultId) || '';
                console.log(`Using V2 vault key for personal ${personal.id} in vault ${vaultId}`);
            } else {
                console.warn(`V2 vault key not found for vault ${vaultId}`);
                // Fallback to V1 method
                vaultKey = personal?.shared_key ? decryptKeyData(personal?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
            }
        } else {
            // V1 decryption: Use shared_key or personal vault key
            vaultKey = personal?.shared_key ? decryptKeyData(personal?.shared_key) : sharedKey ? decryptKeyData(sharedKey) : secureStore.getVaultKey();
        }
        
        try {
            return {
                ...personal,
                encrypted_first_name: personal.encrypted_first_name
                    ? decryptData(decryptBufferData(personal.encrypted_first_name), vaultKey)
                    : '',
                encrypted_last_name: personal.encrypted_last_name
                    ? decryptData(decryptBufferData(personal.encrypted_last_name), vaultKey)
                    : '',
                encrypted_email_address: personal.encrypted_email_address
                    ? decryptData(decryptBufferData(personal.encrypted_email_address), vaultKey)
                    : '',
                encrypted_phone: personal.encrypted_phone
                    ? decryptData(decryptBufferData(personal.encrypted_phone), vaultKey)
                    : '',
                is_shared: sharedKey ? true : false,
                shared_key: sharedKey ? vaultKey : '',
                permission_level: sharedKey ? permissionLevel : '',
                itemType: sharedKey ? 'category' : ''
            };
        } catch (error) {
            console.error('Error decrypting personal details:', error);
            return personal;
        }
    };

    const decryptNotes = (note: any) => {
        const vaultKey = secureStore.getVaultKey();
        try {
            const encryptedNotesString = decryptBufferData(note.encrypted_notes);
            const decryptedNotes = decryptData(encryptedNotesString, vaultKey);
            return {
                ...note,
                encrypted_notes: decryptedNotes || ''
            };
        } catch (error) {
            console.error('Error decrypting notes:', error);
            return {
                ...note,
                encrypted_notes: '(Decryption failed)'
            };
        }
    };

    // Filter functions
    const filterEntries = (entries: IDecryptedPasswordEntry[]) => {
        if (!searchQuery || !entries) return entries || [];

        return entries.filter(entry =>
            entry.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            entry.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            entry.website_url?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const filterNotes = (notes: Note[]) => {
        if (!searchQuery || !notes) return notes || [];

        return notes.filter(note =>
            note.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            note.encrypted_notes?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    };

    const filterDetails = (details: any[]) => {
        if (!searchQuery || !details) return details || [];

        return details.filter(detail =>
            Object.values(detail).some(
                value => typeof value === 'string' && value.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
    };

    useEffect(() => {
        getAllCategoriesBasic();
    }, [getAllCategoriesBasic]);

    useEffect(() => {
        const handleVaultsNavigation = () => {
            handleBackClick();
        };

        window.addEventListener('vaults-navigation-clicked', handleVaultsNavigation);
        return () => {
            window.removeEventListener('vaults-navigation-clicked', handleVaultsNavigation);
        };
    }, [handleBackClick]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Helper function to get counts
    const getItemCount = (type: TabType, categoryId: string | null) => {
        const category = categoriesWithEntries.find(c => c.category_id === categoryId);
        if (!category) return 0;

        switch (type) {
            case 'passwords':
                return category.passwords?.length || 0;
            case 'notes':
                return category.notes?.length || 0;
            case 'cardDetails':
                return category.cards?.length || 0;
            case 'bankDetails':
                return category.bank_details?.length || 0;
            case 'personalDetails':
                return category.personal_details?.length || 0;
            default:
                return 0;
        }
    };

    // Get total count for a category
    // Item types with icons and colors
    const typeOptions = [
        { id: 'passwords' as TabType, name: 'Passwords', icon: <KeyIcon className="h-10 w-10" />, color: 'text-blue-500' },
        { id: 'notes' as TabType, name: 'Secure Notes', icon: <DocumentTextIcon className="h-10 w-10" />, color: 'text-green-500' },
        { id: 'cardDetails' as TabType, name: 'Cards', icon: <CreditCardIcon className="h-10 w-10" />, color: 'text-purple-500' },
        { id: 'bankDetails' as TabType, name: 'Bank Accounts', icon: <BuildingLibraryIcon className="h-10 w-10" />, color: 'text-yellow-500' },
        { id: 'personalDetails' as TabType, name: 'Personal Info', icon: <UserIcon className="h-10 w-10" />, color: 'text-red-500' }
    ];

    // Render breadcrumbs
    const renderBreadcrumbs = () => {
        const categoryName = activeCategory
            ? (activeCategory.name === 'Uncategorized' ? 'Unassigned' : activeCategory.name)
            : '';

        return currentLevel !== 'root' && categoryName ? (
            <nav className="mb-6 flex items-center space-x-2 text-sm text-gray-500">
                <button
                    onClick={handleBackClick}
                    className="hover:text-gray-900"
                >
                    Vaults
                </button>

                <span>/</span>
                <span className="font-medium text-gray-900">{categoryName}</span>
            </nav>
        ) : null;
    };


    // Render root level (all vaults)
    const renderRootLevel = () => {
        // Filter categories based on search query
        // const filteredCategorized = categoriesWithEntries.filter(category => {
        //     if (category.category_id === null) return false; // Exclude unassigned from filtered list
        //     if (!searchQuery) return true;
        //     return category.category_name.toLowerCase().includes(searchQuery.toLowerCase());
        // });
        const filteredCategorized = allCategories.filter(category => {
            if (category.id === null) return false; // Exclude unassigned from filtered list
            if (!searchQuery) return true;
            return category.name.toLowerCase().includes(searchQuery.toLowerCase());
        });

        // Check if we have any real vaults (excluding unassigned)
        const hasVaults = filteredCategorized.length > 0;

        // Check if we have any unassigned items
        const unassignedCategory = allCategories.find(cat => cat.id === null);
        const hasUnassignedItems = unassignedCategory && unassignedCategory.total_count && unassignedCategory.total_count > 0;

        // True empty state is when there are nogetTotalItemCount vaults AND no unassigned items
        const isEmptyState = !hasVaults && !hasUnassignedItems;

        return (
            <>
                <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 min-h-screen">
                    {/* Enhanced Header with International Design - Full Width Banner */}
                    {hasVaults && (
                        <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                            {/* Background Pattern */}
                            <div className="absolute inset-0 opacity-10">
                                <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                                <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                                <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                            </div>

                            <div className="relative px-4 sm:px-6 py-12">
                                <div className="max-w-7xl mx-auto">
                                    {/* Header Content */}
                                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                        <div className="mb-6 lg:mb-0">
                                            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4 font-sans-serif">
                                                Vault Management
                                            </h1>
                                            <p className="text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
                                                Secure storage for your digital assets with enterprise-grade encryption and team collaboration features.
                                            </p>
                                        </div>
                                    </div>

                                    {/* Stats Cards */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="p-2 bg-white/20 rounded-lg">
                                                    <BriefcaseIcon className="w-6 h-6 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-white">
                                                        {filteredCategorized.length}
                                                    </div>
                                                    <div className="text-xs text-white/80 uppercase tracking-wide">
                                                        Total Vaults
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="p-2 bg-white/20 rounded-lg">
                                                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                    </svg>
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-white">
                                                        {filteredCategorized.reduce((sum, cat) => sum + (cat.total_count || 0), 0) + (unassignedCategory?.total_count || 0)}
                                                    </div>
                                                    <div className="text-xs text-white/80 uppercase tracking-wide">
                                                        Total Items
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                            <div className="flex items-center space-x-3">
                                                <div className="p-2 bg-white/20 rounded-lg">
                                                    <ShareIcon className="w-6 h-6 text-white" />
                                                </div>
                                                <div>
                                                    <div className="text-2xl font-bold text-white">
                                                        {filteredCategorized.filter(cat => cat.is_shared).length}
                                                    </div>
                                                    <div className="text-xs text-white/80 uppercase tracking-wide">
                                                        Shared Vaults
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Content Area with proper padding */}
                    <div className="px-4 sm:px-6 lg:px-8">
                        {/* Enhanced Loading State */}
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-16">
                                <div className="relative">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 dark:border-gray-700"></div>
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent absolute top-0 left-0"></div>
                                </div>
                                <p className="text-gray-600 dark:text-gray-400 mt-4 text-lg font-medium">Loading your vaults...</p>
                                <p className="text-gray-500 dark:text-gray-500 text-sm">Securing your digital assets</p>
                            </div>
                        )}

                        {/* Enhanced Empty State */}
                        {!loading && isEmptyState && (
                            <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-200 dark:border-gray-700 mt-8">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800"></div>
                                <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full opacity-20 blur-xl"></div>
                                <div className="absolute bottom-0 left-0 -mb-4 -ml-4 w-32 h-32 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-full opacity-15 blur-2xl"></div>

                                <div className="relative flex flex-col items-center justify-center py-20 px-8">
                                    <div className="mb-8 relative">
                                        <div className="h-24 w-24 bg-gradient-to-br from-primary-600 to-primary-800 rounded-3xl flex items-center justify-center shadow-2xl transform rotate-3">
                                            <BriefcaseIcon className="h-12 w-12 text-white" />
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-orange-400 to-red-500 rounded-full flex items-center justify-center shadow-lg">
                                            <PlusIcon className="h-4 w-4 text-white" />
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">Welcome to Your Secure Vault</h3>
                                    <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-8 text-lg leading-relaxed">
                                        Create your first vault to start organizing and securing your digital assets with enterprise-grade encryption.
                                    </p>

                                    <button
                                        onClick={() => setIsCreateModalOpen(true)}
                                        className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-800 text-white font-semibold rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-blue-500/30"
                                    >
                                        <PlusIcon className="h-6 w-6 mr-3 transition-transform duration-200 group-hover:rotate-90" />
                                        Create Your First Vault
                                        <div className="absolute inset-0 rounded-2xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-200"></div>
                                    </button>

                                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 text-sm text-gray-500 dark:text-gray-400">
                                        <div className="flex items-center">
                                            <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                            Bank-level security
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                                            Team collaboration
                                        </div>
                                        <div className="flex items-center">
                                            <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
                                            Cross-platform sync
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Enhanced Vault Cards Grid with proper spacing */}
                        {(!isEmptyState || loading) && !loading && (
                            <div className="py-8">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                                    {/* Enhanced Unassigned Vault Card */}
                                    {unassignedCategory && unassignedCategory.total_count && unassignedCategory.total_count > 0 && (
                                        <div
                                            onClick={() => handleCategoryClick(unassignedCategory as unknown as ICategory)}
                                            className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-2xl border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-500 cursor-pointer transform hover:-translate-y-2 hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-500/20"
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Open Unassigned vault with ${unassignedCategory.total_count} items`}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleCategoryClick(unassignedCategory as unknown as ICategory);
                                                }
                                            }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-slate-50 to-gray-100 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                            <div className="relative p-6 lg:p-8">
                                                <div className="flex items-start justify-between mb-6">
                                                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                                                        <div className="relative">
                                                            <div className="h-14 w-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-500 dark:text-gray-400 group-hover:scale-110 transition-all duration-300 shadow-lg">
                                                                <BriefcaseIcon className="h-7 w-7" />
                                                            </div>
                                                            <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm">
                                                                <div className="w-2 h-2 bg-white rounded-full"></div>
                                                            </div>
                                                        </div>
                                                        <div className="min-w-0 flex-1">
                                                            <h3
                                                                className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300 truncate"
                                                                title="Unassigned"
                                                            >
                                                                Unassigned
                                                            </h3>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed line-clamp-2">
                                                                Items awaiting organization
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                    <div className="flex items-center flex-wrap gap-3">
                                                        <span className="inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 shadow-sm">
                                                            <div className="w-2 h-2 bg-orange-500 rounded-full mr-2"></div>
                                                            {unassignedCategory.total_count} {unassignedCategory.total_count === 1 ? 'item' : 'items'}
                                                        </span>
                                                    </div>

                                                    <div className="sm:ml-auto opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                                        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 shadow-lg">
                                                            <ChevronRightIcon className="h-4 w-4 text-white" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Enhanced Categorized Vault Cards */}
                                    {filteredCategorized.map((category) => {
                                        const isShared = category.is_shared;
                                        const hasSharing = (category.shared_with_users && category.shared_with_users.length > 0) ||
                                            (category.shared_with_teams && category.shared_with_teams.length > 0);

                                        // Generate dynamic colors based on vault name
                                        const colorVariants = [
                                            { from: 'from-blue-500', to: 'to-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400' },
                                            { from: 'from-purple-500', to: 'to-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/30', text: 'text-purple-600 dark:text-purple-400' },
                                            { from: 'from-green-500', to: 'to-green-600', bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400' },
                                            { from: 'from-orange-500', to: 'to-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400' },
                                            { from: 'from-pink-500', to: 'to-pink-600', bg: 'bg-pink-50 dark:bg-pink-900/30', text: 'text-pink-600 dark:text-pink-400' },
                                            { from: 'from-indigo-500', to: 'to-indigo-600', bg: 'bg-indigo-50 dark:bg-indigo-900/30', text: 'text-indigo-600 dark:text-indigo-400' }
                                        ];
                                        const colorIndex = category.name.charCodeAt(0) % colorVariants.length;
                                        const colors = colorVariants[colorIndex];

                                        return (
                                            <div
                                                key={category.id}
                                                onClick={() => handleCategoryClick(category as unknown as ICategory)}
                                                className={`group relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm hover:shadow-2xl border transition-all duration-500 cursor-pointer transform hover:-translate-y-2 hover:scale-[1.02] focus:outline-none focus:ring-4 focus:ring-blue-500/20 ${isShared
                                                    ? 'border-blue-200 dark:border-blue-800 hover:border-blue-300 dark:hover:border-blue-700 shadow-blue-500/10'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                                    }`}
                                                tabIndex={0}
                                                role="button"
                                                aria-label={`Open ${category.name} vault with ${category.total_count} items`}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        handleCategoryClick(category as unknown as ICategory);
                                                    }
                                                }}
                                            >
                                                {/* Shared Vault Indicator */}
                                                {isShared && (
                                                    <div className="absolute -top-2 -right-2 z-20">
                                                        <div className="flex items-center justify-center w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full shadow-lg border-2 border-white dark:border-gray-800">
                                                            <ShareIcon className="h-4 w-4 text-white" />
                                                        </div>
                                                    </div>
                                                )}

                                                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity duration-500"
                                                    style={{
                                                        background: `linear-gradient(135deg, ${colors.bg.includes('blue') ? 'rgb(219 234 254 / 0.5)' :
                                                            colors.bg.includes('purple') ? 'rgb(243 232 255 / 0.5)' :
                                                                colors.bg.includes('green') ? 'rgb(220 252 231 / 0.5)' :
                                                                    colors.bg.includes('orange') ? 'rgb(255 237 213 / 0.5)' :
                                                                        colors.bg.includes('pink') ? 'rgb(253 242 248 / 0.5)' :
                                                                            'rgb(238 242 255 / 0.5)'} 0%, transparent 100%)`
                                                    }}>
                                                </div>

                                                <div className="relative p-6 lg:p-8">
                                                    <div className="flex items-start justify-between mb-6">
                                                        <div className="flex items-center space-x-4 min-w-0 flex-1 mr-4">
                                                            <div className="relative">
                                                                <div className={`h-14 w-14 flex items-center justify-center rounded-2xl bg-gradient-to-br ${colors.from} ${colors.to} group-hover:scale-110 transition-all duration-300 shadow-lg`}>
                                                                    <BriefcaseIcon className="h-7 w-7 text-white" />
                                                                </div>
                                                                {hasSharing && !isShared && (
                                                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center shadow-sm border-2 border-white dark:border-gray-800">
                                                                        <UsersIcon className="h-3 w-3 text-white" />
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className={`text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:${colors.text.split(' ')[0]} transition-colors duration-300 truncate`} title={category.name}>
                                                                    {category.name}
                                                                </h3>
                                                                {category.description && (
                                                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2 leading-relaxed" title={category.description}>
                                                                        {category.description}
                                                                    </p>
                                                                )}
                                                                {category?.shared_by_email && (
                                                                    <p className={`text-xs ${colors.text} mt-2 truncate font-medium`} title={`Shared by ${category?.shared_by_email}`}>
                                                                        👤 {category?.shared_by_email}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Edit/Delete buttons that appear on hover - Match old code structure */}
                                                        {category.permission_level !== 'read' && (
                                                            <div className="absolute top-4 right-4 flex space-x-2">
                                                                <div
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    className="cursor-pointer"
                                                                >
                                                                    <QuickAddItem
                                                                        categoryId={category.id}
                                                                        onUpdate={getAllCategoriesBasic}
                                                                        isShared={category.is_shared || false}
                                                                        sharedKey={category.shared_key && typeof category.shared_key === 'string' && category.shared_key.length > 0 ? (() => {
                                                                            try {
                                                                                return decryptKeyData(category.shared_key);
                                                                            } catch (error) {
                                                                                console.error('Failed to decrypt shared_key:', error);
                                                                                return '';
                                                                            }
                                                                        })() : ''}
                                                                        categoryName={category.name}
                                                                        onQuickAddItemSelect={handleQuickAddItemSelect}
                                                                    />
                                                                </div>

                                                                <div className="relative">
                                                                    <button
                                                                        onClick={(e) => toggleDropdown(e, category.id)}
                                                                        className="p-1 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500"
                                                                    >
                                                                        <EllipsisVerticalIcon className="h-5 w-5" />
                                                                    </button>

                                                                    {openDropdownId === category.id && (
                                                                        <div
                                                                            ref={dropdownRef}
                                                                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-20 border border-gray-200"
                                                                        >
                                                                            <div className="py-1">
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        setSelectedCategory({
                                                                                            id: category.id,
                                                                                            name: category.name,
                                                                                            description: category.description,
                                                                                            color: category.color,
                                                                                            vault_id: category.vault_id,
                                                                                            is_shared: category.is_shared,
                                                                                            shared_key: category.shared_key,
                                                                                            permission_level: category.permission_level,
                                                                                            created_at: category.created_at,
                                                                                            updated_at: category.updated_at
                                                                                        });
                                                                                        setIsEditModalOpen(true);
                                                                                        setOpenDropdownId(null);
                                                                                    }}
                                                                                    className="w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100"
                                                                                >
                                                                                    <span className="mr-2"><PencilIcon className="h-4 w-4" /></span>
                                                                                    Edit
                                                                                </button>
                                                                                <button
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleDeleteCategoryClick(category.id, category.name);
                                                                                        setOpenDropdownId(null);
                                                                                    }}
                                                                                    className="w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 text-red-600 hover:text-red-700"
                                                                                >
                                                                                    <span className="mr-2"><TrashIcon className="h-4 w-4" /></span>
                                                                                    Delete
                                                                                </button>
                                                                                {!category.is_shared && (
                                                                                    <button
                                                                                        onClick={(e) => {
                                                                                            e.stopPropagation();
                                                                                            setVaultToShare({
                                                                                                id: category.id,
                                                                                                name: category.name
                                                                                            });
                                                                                            setIsShareModalOpen(true);
                                                                                            setOpenDropdownId(null);
                                                                                        }}
                                                                                        className="w-full text-left px-4 py-2 text-sm flex items-center hover:bg-gray-100 text-blue-600 hover:text-blue-700"
                                                                                    >
                                                                                        <span className="mr-2"><ShareIcon className="h-4 w-4" /></span>
                                                                                        Share
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="flex flex-col space-y-4">
                                                        {/* Item Count and Sharing Info in same row */}
                                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                                            <div className="flex items-center flex-wrap gap-3">
                                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-xl text-sm font-medium ${colors.bg} ${colors.text} shadow-sm`}>
                                                                    <div className={`w-2 h-2 rounded-full mr-2 ${colors.text.includes('blue') ? 'bg-blue-500' :
                                                                        colors.text.includes('purple') ? 'bg-purple-500' :
                                                                            colors.text.includes('green') ? 'bg-green-500' :
                                                                                colors.text.includes('orange') ? 'bg-orange-500' :
                                                                                    colors.text.includes('pink') ? 'bg-pink-500' :
                                                                                        'bg-indigo-500'}`}></div>
                                                                    {category.total_count} {category.total_count === 1 ? 'item' : 'items'}
                                                                </span>

                                                                {/* Sharing indicators in same row */}
                                                                {((category.shared_with_users && category.shared_with_users.length > 0) ||
                                                                    (category.shared_with_teams && category.shared_with_teams.length > 0)) && (
                                                                        <>
                                                                            {category.shared_with_users && category.shared_with_users.length > 0 && (
                                                                                <span title="Shared with members" className="inline-flex items-center rounded-full bg-blue-50 dark:bg-blue-900/30 px-2 py-1 text-xs font-medium text-blue-700 dark:text-blue-400">
                                                                                    <UsersIcon className="h-3 w-3 mr-1" />
                                                                                    {category.shared_with_users.length}
                                                                                </span>
                                                                            )}
                                                                            {category.shared_with_teams && category.shared_with_teams.length > 0 && (
                                                                                <span title="Shared with teams" className="inline-flex items-center rounded-full bg-purple-50 dark:bg-purple-900/30 px-2 py-1 text-xs font-medium text-purple-700 dark:text-purple-400">
                                                                                    <UserGroupIcon className="h-3 w-3 mr-1" />
                                                                                    {category.shared_with_teams.length}
                                                                                </span>
                                                                            )}
                                                                        </>
                                                                    )}
                                                            </div>

                                                            <div className="sm:ml-auto opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                                                <div className={`p-2 rounded-lg bg-gradient-to-br ${colors.from} ${colors.to} shadow-lg`}>
                                                                    <ChevronRightIcon className="h-4 w-4 text-white" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* No results message */}
                                    {searchQuery && filteredCategorized.length === 0 && !hasUnassignedItems && (
                                        <div className="col-span-full text-center py-10">
                                            <p className="text-gray-500">No vaults or items match your search.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-primary-500/30 flex items-center justify-center z-10 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 active:scale-95"
                    aria-label="Create Vault"
                >
                    <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                    <span className="ml-2 hidden sm:inline font-semibold">Create Vault</span>
                </button>
            </>
        );
    };

    const toggleDropdown = (e: React.MouseEvent, categoryId: string) => {
        e.stopPropagation();
        if (openDropdownId === categoryId) {
            setOpenDropdownId(null);
        } else {
            setOpenDropdownId(categoryId);
        }
    };

    // Function to get color based on password strength
    const getStrengthColor = (strength: string): string => {
        switch (strength) {
            case 'Very Weak': return 'text-red-500 bg-red-100';
            case 'Weak': return 'text-orange-500 bg-orange-100';
            case 'Fair': return 'text-yellow-500 bg-yellow-100';
            case 'Strong': return 'text-blue-500 bg-blue-100';
            case 'Very Strong': return 'text-green-500 bg-green-100';
            default: return 'text-gray-500 bg-gray-100';
        }
    };

    // Password strength indicator component
    const PasswordStrengthIndicator = ({ strength }: { strength: string }) => {
        return (
            <div className="flex items-center text-xs">
                <div className="flex items-center py-0.5">
                    <span
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0 mr-1"
                        style={{ backgroundColor: getStrengthColor(strength) }}
                    ></span>
                    <span className="text-gray-600 dark:text-gray-400">
                        {strength}
                    </span>
                </div>
            </div>
        );
    };

    // Get the filtered and sorted data for the current tab
    const getFilteredAndSortedData = () => {
        if (!activeCategory) return [];

        switch (activeTab) {
            case 'passwords':
                return getFilteredAndSortedEntries(activeCategory.passwords || []);
            case 'cardDetails':
                return getFilteredAndSortedCards(activeCategory.cards || []);
            case 'bankDetails':
                return getFilteredAndSortedBanks(activeCategory.bank_details || []);
            case 'personalDetails':
                return getFilteredAndSortedPersonals(activeCategory.personal_details || []);
            // case 'notes':
            //     // Notes don't have shared or strength filters
            //     return filterNotes(activeCategory.notes || []);
            default:
                return [];
        }
    };

    // Filter password entries based on selected filters
    const getFilteredAndSortedEntries = (entries: IDecryptedPasswordEntry[]): IDecryptedPasswordEntry[] => {
        // First apply search filter
        let filtered = filterEntries(entries);

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(entry => !entry.is_owner);
        }

        // Apply password strength filter
        if (passwordStrengthFilter !== 'all') {
            const strengthFilterMap: Record<PasswordStrengthFilter, string> = {
                'all': '',
                'veryWeak': 'Very Weak',
                'weak': 'Weak',
                'fair': 'Fair',
                'strong': 'Strong',
                'veryStrong': 'Very Strong'
            };

            filtered = filtered.filter(entry =>
                entry.password_strength === strengthFilterMap[passwordStrengthFilter]
            );
        }

        // Apply sorting
        return getSortedPasswordEntries(filtered);
    };

    // Sort password entries based on selected sort option
    const getSortedPasswordEntries = (entries: IDecryptedPasswordEntry[]): IDecryptedPasswordEntry[] => {
        if (!entries.length) return entries;
        let sorted = [...entries];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) =>
                    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
            case 'alphabetical':
                return sorted.sort((a, b) => a.title.localeCompare(b.title));
            case 'passwordStrength':
                // Map strength to numeric value for sorting
                const strengthMap: Record<string, number> = {
                    'Very Weak': 1,
                    'Weak': 2,
                    'Fair': 3,
                    'Strong': 4,
                    'Very Strong': 5
                };
                return sorted.sort((a, b) => {
                    const strengthA = strengthMap[a.password_strength] || 0;
                    const strengthB = strengthMap[b.password_strength] || 0;
                    return strengthB - strengthA; // Sort from strongest to weakest
                });
            default:
                return sorted;
        }
    };

    // Filter and sort card details
    const getFilteredAndSortedCards = (cards: CardDetail[]): CardDetail[] => {
        // First apply search filter
        let filtered = filterDetails(cards);

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(card => !card.is_owner);
        }

        // Apply sorting
        return getSortedCards(filtered);
    };

    // Sort card details
    const getSortedCards = (cards: CardDetail[]): CardDetail[] => {
        if (!cards.length) return cards;
        let sorted = [...cards];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Filter and sort bank details
    const getFilteredAndSortedBanks = (banks: BankDetail[]): BankDetail[] => {
        // First apply search filter
        let filtered = filterDetails(banks);

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(bank => !bank.is_owner);
        }

        // Apply sorting
        return getSortedBanks(filtered);
    };

    // Sort bank details
    const getSortedBanks = (banks: BankDetail[]): BankDetail[] => {
        if (!banks.length) return banks;
        let sorted = [...banks];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Filter and sort personal details
    const getFilteredAndSortedPersonals = (personals: PersonalDetail[]): PersonalDetail[] => {
        // First apply search filter
        let filtered = filterDetails(personals);

        // Apply shared filter
        if (showSharedOnly) {
            filtered = filtered.filter(personal => !personal.is_owner);
        }

        // Apply sorting
        return getSortedPersonals(filtered);
    };

    // Sort personal details
    const getSortedPersonals = (personals: PersonalDetail[]): PersonalDetail[] => {
        if (!personals.length) return personals;
        let sorted = [...personals];

        switch (sortOption) {
            case 'newest':
                return sorted.sort((a, b) => {
                    const dateA = new Date(a.created_at).getTime();
                    const dateB = new Date(b.created_at).getTime();
                    return dateB - dateA;
                });
            case 'alphabetical':
                return sorted.sort((a, b) => {
                    const nameA = a.name || '';
                    const nameB = b.name || '';
                    return nameA.localeCompare(nameB);
                });
            default:
                return sorted;
        }
    };

    // Apply filter settings from temporary state to active state
    const applyFilters = () => {
        setPasswordStrengthFilter(tempPasswordStrengthFilter);
        setShowSharedOnly(tempShowSharedOnly);
        setIsFilterMenuOpen(false);
    };

    // Reset temporary filter settings
    const resetFilters = () => {
        setTempPasswordStrengthFilter('all');
        setTempShowSharedOnly(false);
    };

    // Clear all filters
    const clearAllFilters = () => {
        setPasswordStrengthFilter('all');
        setShowSharedOnly(false);
    };

    // Handle clicks outside the menus
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (sortMenuRef.current && !sortMenuRef.current.contains(event.target as Node)) {
                setIsSortMenuOpen(false);
            }
            if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node)) {
                if (isFilterMenuOpen) {
                    resetFilters();
                }
                setIsFilterMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterMenuOpen]);

    // Sort Menu Component
    const SortMenu = () => {
        const getTabLabel = () => {
            switch (activeTab) {
                case 'passwords': return 'Passwords';
                case 'cardDetails': return 'Cards';
                case 'bankDetails': return 'Bank Accounts';
                case 'personalDetails': return 'Personal Details';
                case 'notes': return 'Notes';
                default: return 'Items';
            }
        };

        return (
            <div
                ref={sortMenuRef}
                className="relative"
            >
                <button
                    type="button"
                    className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mr-2"
                    onClick={() => {
                        setIsSortMenuOpen(!isSortMenuOpen);
                        setIsFilterMenuOpen(false);
                    }}
                >
                    <ArrowsUpDownIcon className="h-5 w-5 mr-1 text-gray-500" />
                    Sort
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isSortMenuOpen && (
                    <div className="absolute mt-2 right-0 w-64 bg-white shadow-lg rounded-md overflow-hidden z-10 border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Sort by</span>
                        </div>

                        {/* Sort Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <button
                                    className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'newest' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                                    onClick={() => {
                                        setSortOption('newest');
                                        setIsSortMenuOpen(false);
                                    }}
                                >
                                    <span>Newest</span>
                                    {sortOption === 'newest' && <ArrowDownIcon className="h-4 w-4" />}
                                </button>
                                <button
                                    className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'alphabetical' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                                    onClick={() => {
                                        setSortOption('alphabetical');
                                        setIsSortMenuOpen(false);
                                    }}
                                >
                                    <span>Alphabetical (A-Z)</span>
                                    {sortOption === 'alphabetical' && <ArrowUpIcon className="h-4 w-4" />}
                                </button>
                                {activeTab === 'passwords' && (
                                    <button
                                        className={`flex items-center justify-between w-full px-3 py-2 text-left text-sm rounded-md ${sortOption === 'passwordStrength' ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50'}`}
                                        onClick={() => {
                                            setSortOption('passwordStrength');
                                            setIsSortMenuOpen(false);
                                        }}
                                    >
                                        <span>Password Strength</span>
                                        {sortOption === 'passwordStrength' && <ArrowDownIcon className="h-4 w-4" />}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Filter Menu Component
    const FilterMenu = () => {
        const getTabLabel = () => {
            switch (activeTab) {
                case 'passwords': return 'passwords';
                case 'cardDetails': return 'cards';
                case 'bankDetails': return 'bank accounts';
                case 'personalDetails': return 'personal details';
                case 'notes': return 'notes';
                default: return 'items';
            }
        };

        return (
            <div
                ref={filterMenuRef}
                className="relative"
            >
                <button
                    onClick={() => {
                        setIsFilterMenuOpen(!isFilterMenuOpen);
                        setIsSortMenuOpen(false);
                        // Initialize temporary filter state with current filter values
                        setTempPasswordStrengthFilter(passwordStrengthFilter);
                        setTempShowSharedOnly(showSharedOnly);
                    }}
                    className="flex items-center px-3 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <FunnelIcon className="h-5 w-5 mr-1 text-gray-500" />
                    Filter
                    {(passwordStrengthFilter !== 'all' || showSharedOnly) && (
                        <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {(passwordStrengthFilter !== 'all' && showSharedOnly) ? '2' : '1'}
                        </span>
                    )}
                    <ChevronDownIcon className="h-4 w-4 ml-1" />
                </button>

                {isFilterMenuOpen && (
                    <div className="absolute mt-2 right-0 w-72 bg-white shadow-lg rounded-md overflow-hidden z-10 border border-gray-200">
                        <div className="flex justify-between items-center p-3 border-b border-gray-200">
                            <span className="font-medium">Filter {getTabLabel()}</span>
                            <button
                                onClick={resetFilters}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                Reset
                            </button>
                        </div>

                        {/* Filter Options */}
                        <div className="p-3">
                            <div className="space-y-2">
                                <div className="flex items-center">
                                    <input
                                        id="sharedOnly"
                                        type="checkbox"
                                        checked={tempShowSharedOnly}
                                        onChange={(e) => setTempShowSharedOnly(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="sharedOnly" className="ml-2 block text-sm text-gray-700">
                                        Shared with me only
                                    </label>
                                </div>

                                {/* Password Strength Filter - Only show for Passwords tab */}
                                {activeTab === 'passwords' && (
                                    <div className="mt-3">
                                        <h4 className="text-xs font-medium text-gray-500 mb-2">Password Strength</h4>
                                        <div className="grid grid-cols-2 gap-2">
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'all' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                                                onClick={() => setTempPasswordStrengthFilter('all')}
                                            >
                                                All Strengths
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryWeak' ? 'border border-red-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryWeak' ? 'Very Weak' : '')}`}
                                                onClick={() => setTempPasswordStrengthFilter('veryWeak')}
                                            >
                                                Very Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'weak' ? 'border border-orange-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'weak' ? 'Weak' : '')}`}
                                                onClick={() => setTempPasswordStrengthFilter('weak')}
                                            >
                                                Weak
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'fair' ? 'border border-yellow-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'fair' ? 'Fair' : '')}`}
                                                onClick={() => setTempPasswordStrengthFilter('fair')}
                                            >
                                                Fair
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'strong' ? 'border border-green-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'strong' ? 'Strong' : '')}`}
                                                onClick={() => setTempPasswordStrengthFilter('strong')}
                                            >
                                                Strong
                                            </button>
                                            <button
                                                className={`px-3 py-1.5 text-xs rounded-md ${tempPasswordStrengthFilter === 'veryStrong' ? 'border border-purple-200' : ''} ${getStrengthColor(tempPasswordStrengthFilter === 'veryStrong' ? 'Very Strong' : '')}`}
                                                onClick={() => setTempPasswordStrengthFilter('veryStrong')}
                                            >
                                                Very Strong
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="p-3 border-t border-gray-200">
                            <button
                                onClick={applyFilters}
                                className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // Handle revoking access for a user or team
    const handleRevokeClick = async (id: string, type: 'user' | 'team', name: string, email?: string) => {
        setConfirmModalType('revoke');
        setCurrentShareType(type);
        setCurrentShareId(id);
        setCurrentSharedEntity({
            id,
            name,
            email
        });
        setShowConfirmModal(true);
    };

    // Confirm revoke action
    const handleConfirmRevoke = async () => {
        try {
            setIsProcessing(true);
            await ShareService.revokeItemAccess(currentShareId, activeCategoryId || '', currentShareType || '');
            toast.success('Access revoked successfully');
            if (currentShareId && (currentShareType === 'user' || currentShareType === 'team')) {
                removeLocalShareEntity(currentShareType, currentShareId);
            }
            setShowSharingDetailsModal(false);
        } catch (error) {
            console.error('Failed to revoke access:', error);
            toast.error('Failed to revoke access');
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    };

    // Handle permission level change
    const handlePermissionChange = (shareId: string, entityName: string, newPerm: string, type: 'user' | 'team', email?: string) => {
        setConfirmModalType('permission');
        setCurrentShareId(shareId);
        setCurrentShareType(type);
        setCurrentSharedEntity({
            id: shareId,
            name: entityName,
            email
        });
        setNewPermission(newPerm);
        setShowConfirmModal(true);
    };

    // Confirm permission change
    const handleConfirmPermissionChange = async () => {
        try {
            setIsProcessing(true);
            await ShareService.updatePermissionLevel(currentShareId, newPermission, activeCategoryId || '', currentShareType || '');
            toast.success('Permission updated successfully');
            if (currentShareId && (currentShareType === 'user' || currentShareType === 'team')) {
                applyLocalPermissionUpdate(currentShareType, currentShareId, newPermission);
            }
        } catch (error) {
            console.error('Failed to update permission:', error);
            toast.error('Failed to update permission');
        } finally {
            setIsProcessing(false);
            setShowConfirmModal(false);
        }
    };

    // Filter shared users and teams based on search query in the sharing modal
    const getFilteredSharedUsers = (): { id: string; email: string; permission_level: string }[] => {
        if (!activeCategory?.shared_with_users) return [];
        if (!sharingSearchQuery) return activeCategory.shared_with_users;

        return activeCategory.shared_with_users.filter(user =>
            user.email.toLowerCase().includes(sharingSearchQuery.toLowerCase())
        );
    };

    const getFilteredSharedTeams = (): { id: string; name: string; permission_level: string }[] => {
        if (!activeCategory?.shared_with_teams) return [];
        if (!sharingSearchQuery) return activeCategory.shared_with_teams;

        return activeCategory.shared_with_teams.filter(team =>
            team.name.toLowerCase().includes(sharingSearchQuery.toLowerCase())
        );
    };

    // Handle item creation from QuickAdd
    const handleQuickAddItemSelect = (categoryId: string, itemType: string) => {
        setSelectedCategoryForModal(categoryId);

        // Get the category info for all types
        const category = categoriesWithEntries.find(cat => cat.category_id === categoryId);
        if (category) {
            setSelectedCategoryInfo({
                id: categoryId,
                isShared: category.is_shared || false,
                sharedKey: category.shared_key && typeof category.shared_key === 'string' && category.shared_key.length > 0 ? (() => {
                    try {
                        return decryptKeyData(category.shared_key);
                    } catch (error) {
                        console.error('Failed to decrypt shared_key:', error);
                        return undefined;
                    }
                })() : undefined,
                permissionLevel: category.permission_level
            });
        }

        switch (itemType) {
            case 'password':
                setQuickAddPasswordModalOpen(true);
                break;
            case 'cardDetails':
                setQuickAddCardModalOpen(true);
                break;
            case 'bankDetails':
                setQuickAddBankModalOpen(true);
                break;
            case 'personalDetails':
                setQuickAddPersonalModalOpen(true);
                break;
        }
    };

    // Handle successful creation
    const handleQuickAddSuccess = () => {
        // Close all creation modals
        setQuickAddPasswordModalOpen(false);
        setQuickAddCardModalOpen(false);
        setQuickAddBankModalOpen(false);
        setQuickAddPersonalModalOpen(false);
        setSelectedCategoryForModal(null);
        setSelectedCategoryInfo(null);
        if (currentLevel === 'root') {
            getAllCategoriesBasic();
        } else {
            refreshCurrentCategory();
        }
        // toast.success('Item created successfully');
    };

    // Render category level (with tabs like VaultPage)
    const renderCategoryLevel = () => {
        if (!activeCategory) return null;

        // Calculate stats for the active category
        const passwordsCount = activeCategory.passwords ? activeCategory.passwords.length : 0;
        const cardsCount = activeCategory.cards ? activeCategory.cards.length : 0;
        const banksCount = activeCategory.bank_details ? activeCategory.bank_details.length : 0;
        const personalsCount = activeCategory.personal_details ? activeCategory.personal_details.length : 0;

        return (
            <div className="bg-gray-50 dark:bg-gray-900 min-h-screen">
                {/* Hero Banner Section matching VaultPage */}
                <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                        <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                        <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                    </div>

                    <div className="relative px-4 sm:px-6 py-12">
                        <div className="max-w-7xl mx-auto">
                            {/* Header Content */}
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
                                <div className="mb-6 lg:mb-0">
                                    <div className="flex items-center mb-4">
                                        <button
                                            onClick={handleBackClick}
                                            className="mr-4 p-2 rounded-md hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                                        >
                                            <ArrowLeftIcon className="h-6 w-6" />
                                        </button>
                                        <h1 className="text-4xl lg:text-5xl font-bold text-white font-sans-serif">Vaults/{activeCategory.name === 'Uncategorized' ? 'Unassigned' : activeCategory.name}</h1>
                                    </div>
                                    <p className="text-xl text-white/90 max-w-2xl leading-relaxed font-sans">
                                        {activeCategory.is_shared ?
                                            `Shared by ${activeCategory.shared_by_email} • Manage your secure items in this vault` :
                                            'Manage your secure items in this vault with confidence and security'
                                        }
                                    </p>

                                    {/* Show sharing info */}
                                    {((activeCategory.shared_with_users && activeCategory.shared_with_users.length > 0) ||
                                        (activeCategory.shared_with_teams && activeCategory.shared_with_teams.length > 0)) && (
                                            <div className="mt-4 flex items-center">
                                                <ShareIcon className="h-5 w-5 text-white/80 mr-2" />
                                                <div className="flex items-center space-x-2">
                                                    {activeCategory.shared_with_users && activeCategory.shared_with_users.length > 0 && (
                                                        <span title="Shared with members" className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 text-sm font-medium text-white border border-white/30">
                                                            <UsersIcon className="h-4 w-4 mr-1" />
                                                            {activeCategory.shared_with_users.length} members
                                                        </span>
                                                    )}
                                                    {activeCategory.shared_with_teams && activeCategory.shared_with_teams.length > 0 && (
                                                        <span title="Shared with teams" className="inline-flex items-center rounded-full bg-white/20 backdrop-blur-sm px-3 py-1 text-sm font-medium text-white border border-white/30">
                                                            <UserGroupIcon className="h-4 w-4 mr-1" />
                                                            {activeCategory.shared_with_teams.length} teams
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Enhanced Sharing details button */}
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowSharingDetailsModal(true);
                                                    }}
                                                    className="ml-3 inline-flex items-center px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white text-sm font-medium rounded-lg border border-white/30 hover:border-white/40 transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-white/50"
                                                    aria-label="View sharing details"
                                                >
                                                    <EyeIcon className="h-4 w-4 mr-1.5" />
                                                    <span>View Details</span>
                                                </button>
                                            </div>
                                        )}
                                </div>

                                {/* Action Controls */}
                                {/* <div className="flex items-center space-x-4">
                                    <div className="hidden lg:flex space-x-3 relative z-40">
                                        <SortMenu />
                                        <FilterMenu />
                                    </div>
                                </div> */}
                            </div>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-white">
                                                {passwordsCount}
                                            </div>
                                            <div className="text-xs text-white/80 uppercase tracking-wide">
                                                Passwords
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-white">
                                                {cardsCount}
                                            </div>
                                            <div className="text-xs text-white/80 uppercase tracking-wide">
                                                Cards
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-white">
                                                {banksCount}
                                            </div>
                                            <div className="text-xs text-white/80 uppercase tracking-wide">
                                                Banks
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
                                    <div className="flex items-center space-x-3">
                                        <div className="p-2 bg-white/20 rounded-lg">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-white">
                                                {personalsCount}
                                            </div>
                                            <div className="text-xs text-white/80 uppercase tracking-wide">
                                                Personal
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Modern Tab Navigation */}
                            <div className="flex items-center justify-center px-4 sm:px-0">
                                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-1 sm:p-2 border border-white/30 w-full sm:w-auto sm:max-w-none overflow-x-auto">
                                    <nav className="flex space-x-1 sm:space-x-2 min-w-max sm:min-w-0 sm:justify-center">
                                        <button
                                            onClick={() => handleTabChange('passwords')}
                                            className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'passwords'
                                                ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-1 sm:space-x-2">
                                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                                </svg>
                                                <span className="hidden sm:inline">Passwords</span>
                                                <span className="sm:hidden">Pass</span>
                                            </div>
                                            {activeTab === 'passwords' && (
                                                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                            )}
                                        </button>

                                        <button
                                            onClick={() => handleTabChange('cardDetails')}
                                            className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'cardDetails'
                                                ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-1 sm:space-x-2">
                                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                </svg>
                                                <span>Cards</span>
                                            </div>
                                            {activeTab === 'cardDetails' && (
                                                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                            )}
                                        </button>

                                        <button
                                            onClick={() => handleTabChange('bankDetails')}
                                            className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'bankDetails'
                                                ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-1 sm:space-x-2">
                                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <span className="hidden sm:inline">Bank Accounts</span>
                                                <span className="sm:hidden">Banks</span>
                                            </div>
                                            {activeTab === 'bankDetails' && (
                                                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                            )}
                                        </button>

                                        <button
                                            onClick={() => handleTabChange('personalDetails')}
                                            className={`relative px-3 sm:px-6 py-2 sm:py-3 rounded-xl font-medium text-xs sm:text-sm transition-all duration-300 whitespace-nowrap ${activeTab === 'personalDetails'
                                                ? 'bg-white text-primary-600 shadow-lg scale-105'
                                                : 'text-white/80 hover:text-white hover:bg-white/20'
                                                }`}
                                        >
                                            <div className="flex items-center space-x-1 sm:space-x-2">
                                                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <span className="hidden sm:inline">Personal Info</span>
                                                <span className="sm:hidden">Personal</span>
                                            </div>
                                            {activeTab === 'personalDetails' && (
                                                <div className="absolute -top-0.5 -right-0.5 sm:-top-1 sm:-right-1 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full border-1 sm:border-2 border-white"></div>
                                            )}
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Sharing Details Modal */}
                {showSharingDetailsModal && (
                    <div className="fixed inset-0 z-[1000] overflow-y-auto">
                        {/* Enhanced Backdrop */}
                        <div className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"></div>
                        
                        {/* Modal Container */}
                        <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                            <div className="relative bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl w-full max-w-lg mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100 dark:border-gray-700 transform transition-all">
                                
                                {/* Enhanced Header */}
                                <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-6 py-4 sm:py-5">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 sm:p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                                <ShareIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                            </div>
                                            <div>
                                                <h3 className="text-lg sm:text-xl font-bold">Sharing Details</h3>
                                                <p className="text-primary-100 text-xs sm:text-sm">
                                                    Manage vault access and permissions
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => {
                                                setShowSharingDetailsModal(false);
                                                setSharingSearchQuery('');
                                            }}
                                            className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200"
                                            aria-label="Close modal"
                                        >
                                            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="bg-gray-50 dark:bg-gray-900 max-h-[calc(90vh-120px)] sm:max-h-[calc(95vh-140px)] overflow-y-auto">
                                    <div className="p-4 sm:p-6 space-y-6">
                                        
                                        {/* Invite Section */}
                                        {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-5 border border-gray-200 dark:border-gray-700 shadow-sm">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div>
                                                        <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Invite Collaborators</h4>
                                                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Share this vault with team members</p>
                                                    </div>
                                                    <button
                                                        onClick={() => {
                                                            setVaultToShare({
                                                                id: activeCategoryId || '',
                                                                name: activeCategory.name
                                                            });
                                                            setIsShareModalOpen(true);
                                                            setShowSharingDetailsModal(false);
                                                            setSharingSearchQuery('');
                                                        }}
                                                        className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-lg shadow-sm hover:shadow-md transform transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus:ring-4 focus:ring-primary-500/30"
                                                    >
                                                        <div className="flex items-center space-x-2">
                                                            <UserPlusIcon className="h-4 w-4" />
                                                            <span>Invite</span>
                                                        </div>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Search Bar */}
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                                                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                                            </div>
                                            <input
                                                type="text"
                                                placeholder="Search by team name or email..."
                                                value={sharingSearchQuery}
                                                onChange={(e) => setSharingSearchQuery(e.target.value)}
                                                className="block w-full pl-12 pr-4 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 dark:focus:border-primary-400 transition-all duration-200"
                                            />
                                            {sharingSearchQuery && (
                                                <div className="absolute inset-y-0 right-0 pr-4 flex items-center">
                                                    <button
                                                        onClick={() => setSharingSearchQuery('')}
                                                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                                                    >
                                                        <XMarkIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* People with Access Section */}
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">People with Access</h4>
                                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                                    {(getFilteredSharedUsers().length + getFilteredSharedTeams().length)} total
                                                </span>
                                            </div>
                                            
                                            <div className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 overflow-hidden max-h-[40vh] overflow-y-auto">
                                                {(getFilteredSharedUsers().length === 0 && getFilteredSharedTeams().length === 0) ? (
                                                    <div className="p-8 text-center">
                                                        <div className="mx-auto h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                                            <UsersIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                                        </div>
                                                        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">No shared access</h3>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">This vault is not shared with anyone yet.</p>
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                                                        {/* Owner Section */}
                                                        <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                                                            <div className="flex items-center space-x-3">
                                                                <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                                                    <UserIcon className="h-4 w-4 text-white" />
                                                                </div>
                                                                <div className="flex-1">
                                                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">You (Owner)</p>
                                                                    <p className="text-xs text-gray-600 dark:text-gray-400">Full access to this vault</p>
                                                                </div>
                                                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800">
                                                                    Owner
                                                                </span>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Shared Users and Teams */}
                                                        {getFilteredSharedUsers().map(user => (
                                                            <div key={user.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center space-x-3">
                                                                        <div className="h-8 w-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-full flex items-center justify-center">
                                                                            <span className="text-xs font-bold text-white">{user.email.charAt(0).toUpperCase()}</span>
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{user.email}</p>
                                                                            <p className="text-xs text-gray-600 dark:text-gray-400">Team member</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2">
                                                                        {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                                            <>
                                                                                <select
                                                                                    value={user.permission_level}
                                                                                    onChange={(e) => handlePermissionChange(user.id, user.email, e.target.value, 'user', user.email)}
                                                                                    className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                                                                                >
                                                                                    <option value="read">Can view</option>
                                                                                    <option value="write">Can edit</option>
                                                                                </select>
                                                                                <button
                                                                                    onClick={() => handleRevokeClick(user.id, 'user', user.email)}
                                                                                    className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                                    title="Revoke access"
                                                                                >
                                                                                    <XMarkIcon className="h-4 w-4" />
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        {!(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                                user.permission_level === 'write' 
                                                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                                                                            }`}>
                                                                                {user.permission_level === 'write' ? 'Can edit' : 'View only'}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {getFilteredSharedTeams().map(team => (
                                                            <div key={team.id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                                <div className="flex items-center justify-between">
                                                                    <div className="flex items-center space-x-3">
                                                                        <div className="h-8 w-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center">
                                                                            <UserGroupIcon className="h-4 w-4 text-white" />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{team.name}</p>
                                                                            <p className="text-xs text-gray-600 dark:text-gray-400">Team</p>
                                                                        </div>
                                                                    </div>
                                                                    <div className="flex items-center space-x-2">
                                                                        {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                                            <>
                                                                                <select
                                                                                    value={team.permission_level}
                                                                                    onChange={(e) => handlePermissionChange(team.id, team.name, e.target.value, 'team')}
                                                                                    className="text-xs px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                                                                                >
                                                                                    <option value="read">Can view</option>
                                                                                    <option value="write">Can edit</option>
                                                                                </select>
                                                                                <button
                                                                                    onClick={() => handleRevokeClick(team.id, 'team', team.name)}
                                                                                    className="p-1.5 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                                    title="Revoke access"
                                                                                >
                                                                                    <XMarkIcon className="h-4 w-4" />
                                                                                </button>
                                                                            </>
                                                                        )}
                                                                        {!(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                                                team.permission_level === 'write' 
                                                                                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                                                                                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border border-gray-200 dark:border-gray-600'
                                                                            }`}>
                                                                                {team.permission_level === 'write' ? 'Can edit' : 'View only'}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}

                                                        {/* Empty state for search */}
                                                        {sharingSearchQuery && getFilteredSharedUsers().length === 0 && getFilteredSharedTeams().length === 0 && (
                                                            <div className="p-8 text-center">
                                                                <div className="mx-auto h-12 w-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                                                                    <MagnifyingGlassIcon className="h-6 w-6 text-gray-400 dark:text-gray-500" />
                                                                </div>
                                                                <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">No matches found</h3>
                                                                <p className="text-xs text-gray-500 dark:text-gray-400">No results for "{sharingSearchQuery}"</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="sticky bottom-0 bg-white dark:bg-gray-800 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                                        <button
                                            type="button"
                                            className="px-6 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors duration-200 focus:outline-none focus:ring-4 focus:ring-gray-500/20"
                                            onClick={() => {
                                                setShowSharingDetailsModal(false);
                                                setSharingSearchQuery('');
                                            }}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Content Section matching VaultPage */}
                <div className="container mx-auto px-6 py-8">
                    {/* Mobile Sort/Filter Controls */}
                    <div className="lg:hidden mb-6 flex justify-end space-x-2 relative z-40">
                        <SortMenu />
                        <FilterMenu />
                    </div>

                    {/* Tab Content */}
                    <div className="space-y-6">
                        {/* Passwords Tab */}
                        {activeTab === 'passwords' && (
                            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {loadingPasswords ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                    </div>
                                ) : (
                                    <>
                                        {activeCategory.passwords && activeCategory.passwords.length > 0 ? (
                                            <PasswordList
                                                entries={getFilteredAndSortedEntries(activeCategory.passwords || []).map(password => ({
                                                    ...password,
                                                    category_id: activeCategoryId === null ? undefined : activeCategoryId,
                                                    permission_level: activeCategory.permission_level,
                                                    itemType: 'category'
                                                }))}
                                                showCategoryBadge={false}
                                                isShared={activeCategory.is_shared ? true : false}
                                                permissionLevel={permissionLevel}
                                                onSuccessfulUpdate={refreshCurrentCategory}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12">
                                                <KeyIcon className="h-16 w-16 text-gray-300 mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900">No passwords found</h3>
                                                <p className="text-sm text-gray-500 mt-1 mb-4">
                                                    Add passwords to this vault to access them securely
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Card Details Tab */}
                        {activeTab === 'cardDetails' && (
                            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {loadingCards ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                    </div>
                                ) : (
                                    <>
                                        {activeCategory.cards && activeCategory.cards.length > 0 ? (
                                            <CardDetailsList
                                                details={getFilteredAndSortedCards(activeCategory.cards || [])}
                                                onUpdate={refreshCurrentCategory}
                                                onEdit={(card) => {
                                                    const cardWithType = {
                                                        ...card,
                                                        sharedKey: selectedSharedKey,
                                                        itemType: 'category' as 'category'
                                                    };
                                                    setSelectedCard(cardWithType);
                                                    setIsCardModalOpen(true);
                                                }}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12">
                                                <CreditCardIcon className="h-16 w-16 text-gray-300 mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900">No cards found</h3>
                                                <p className="text-sm text-gray-500 mt-1 mb-4">
                                                    Add cards to this vault to access them securely
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Bank Details Tab */}
                        {activeTab === 'bankDetails' && (
                            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {loadingBanks ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                    </div>
                                ) : (
                                    <>
                                        {activeCategory.bank_details && activeCategory.bank_details.length > 0 ? (
                                            <BankDetailsList
                                                details={getFilteredAndSortedBanks(activeCategory.bank_details || [])}
                                                onUpdate={refreshCurrentCategory}
                                                onEdit={(bank) => {
                                                    const bankWithType = {
                                                        ...bank,
                                                        sharedKey: selectedSharedKey,
                                                        isSharedBank: activeCategory.is_shared ? true : false,
                                                        itemType: 'category' as 'category'
                                                    };
                                                    setSelectedBank(bankWithType);
                                                    setIsBankModalOpen(true);
                                                }}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12">
                                                <BuildingLibraryIcon className="h-16 w-16 text-gray-300 mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900">No bank accounts found</h3>
                                                <p className="text-sm text-gray-500 mt-1 mb-4">
                                                    Add bank accounts to this vault to access them securely
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Personal Details Tab */}
                        {activeTab === 'personalDetails' && (
                            <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700">
                                {loadingPersonals ? (
                                    <div className="flex justify-center items-center py-12">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                                    </div>
                                ) : (
                                    <>
                                        {activeCategory.personal_details && activeCategory.personal_details.length > 0 ? (
                                            <PersonalDetailsList
                                                details={getFilteredAndSortedPersonals(activeCategory.personal_details || [])}
                                                onUpdate={refreshCurrentCategory}
                                                onEdit={(personal) => {
                                                    const personalWithType = {
                                                        ...personal,
                                                        sharedKey: selectedSharedKey,
                                                        isSharedPersonal: activeCategory.is_shared ? true : false,
                                                        itemType: 'category' as 'category'
                                                    };
                                                    setSelectedPersonal(personalWithType);
                                                    setIsPersonalModalOpen(true);
                                                }}
                                            />
                                        ) : (
                                            <div className="flex flex-col items-center justify-center py-12">
                                                <UserIcon className="h-16 w-16 text-gray-300 mb-4" />
                                                <h3 className="text-lg font-medium text-gray-900">No personal details found</h3>
                                                <p className="text-sm text-gray-500 mt-1 mb-4">
                                                    Add personal details to this vault to access them securely
                                                </p>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
                {showSharingDetailsModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center p-4 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900">Sharing details</h3>
                                <button
                                    onClick={() => {
                                        setShowSharingDetailsModal(false);
                                        setSharingSearchQuery('');
                                    }}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <XMarkIcon className="h-5 w-5" />
                                </button>
                            </div>

                            <div className="p-4 overflow-y-auto flex-1">
                                {/* Invite section */}
                                {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                    <div className="mb-6">
                                        <div className="flex items-center justify-between mb-2">
                                            <h4 className="text-sm font-medium text-gray-700">Invite people or teams</h4>
                                            <button
                                                onClick={() => {
                                                    setVaultToShare({
                                                        id: activeCategoryId || '',
                                                        name: activeCategory.name
                                                    });
                                                    setIsShareModalOpen(true);
                                                    setShowSharingDetailsModal(false);
                                                    setSharingSearchQuery('');
                                                }}
                                                className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                                            >
                                                Invite
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Search bar */}
                                <div className="mb-4 relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Search by team name or email"
                                        value={sharingSearchQuery}
                                        onChange={(e) => setSharingSearchQuery(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    />
                                    {sharingSearchQuery && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                                            <button
                                                onClick={() => setSharingSearchQuery('')}
                                                className="text-gray-400 hover:text-gray-500"
                                            >
                                                <XMarkIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Shared with section */}
                                <div>
                                    <h4 className="text-sm font-medium text-gray-700 mb-2">People with access</h4>
                                    <div className="bg-gray-50 rounded-md border border-gray-200 overflow-hidden max-h-[50vh] overflow-y-auto">
                                        {/* Users */}
                                        {getFilteredSharedUsers().map(user => (
                                            <div key={user.id} className="flex justify-between items-center p-3 border-b border-gray-200 hover:bg-gray-50">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-medium text-sm">
                                                        {user.email.charAt(0).toUpperCase()}
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-gray-900">{user.email}</p>
                                                        <p className="text-xs text-gray-500">User</p>
                                                    </div>
                                                </div>
                                                {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                    <div className="flex items-center space-x-2">
                                                        <select
                                                            value={user.permission_level}
                                                            onChange={(e) => handlePermissionChange(user.id, user.email, e.target.value, 'user', user.email)}
                                                            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white"
                                                        >
                                                            <option value="read">Can view</option>
                                                            <option value="write">Can edit</option>
                                                        </select>
                                                        <button
                                                            onClick={() => handleRevokeClick(user.id, 'user', user.email)}
                                                            className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            title="Revoke access"
                                                        >
                                                            <XMarkIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Teams */}
                                        {getFilteredSharedTeams().map(team => (
                                            <div key={team.id} className="flex justify-between items-center p-3 border-b border-gray-200 hover:bg-gray-50">
                                                <div className="flex items-center">
                                                    <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 font-medium text-sm">
                                                        <UserGroupIcon className="h-4 w-4" />
                                                    </div>
                                                    <div className="ml-3">
                                                        <p className="text-sm font-medium text-gray-900">{team.name}</p>
                                                        <p className="text-xs text-gray-500">Team</p>
                                                    </div>
                                                </div>
                                                {(!activeCategory.is_shared || hasPermission(permissions, 'vault', 'vault_share_teams')) && (
                                                    <div className="flex items-center space-x-2">
                                                        <select
                                                            value={team.permission_level}
                                                            onChange={(e) => handlePermissionChange(team.id, team.name, e.target.value, 'team')}
                                                            className="text-xs px-2 py-1 rounded border border-gray-300 bg-white"
                                                        >
                                                            <option value="read">Can view</option>
                                                            <option value="write">Can edit</option>
                                                        </select>
                                                        <button
                                                            onClick={() => handleRevokeClick(team.id, 'team', team.name)}
                                                            className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-50"
                                                            title="Revoke access"
                                                        >
                                                            <XMarkIcon className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* Empty states */}
                                        {/* No matches for search */}
                                        {sharingSearchQuery && getFilteredSharedUsers().length === 0 && getFilteredSharedTeams().length === 0 && (
                                            <div className="p-4 text-center">
                                                <p className="text-sm text-gray-500">No matches found for "{sharingSearchQuery}"</p>
                                            </div>
                                        )}

                                        {/* Not shared with anyone */}
                                        {!sharingSearchQuery && (!activeCategory.shared_with_users || activeCategory.shared_with_users.length === 0) &&
                                            (!activeCategory.shared_with_teams || activeCategory.shared_with_teams.length === 0) && (
                                                <div className="p-4 text-center">
                                                    <p className="text-sm text-gray-500">This vault is not shared with anyone</p>
                                                </div>
                                            )}
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gray-50 px-4 py-3 sm:px-6 flex justify-end rounded-b-lg border-t border-gray-200">
                                <button
                                    type="button"
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    onClick={() => {
                                        setShowSharingDetailsModal(false);
                                        setSharingSearchQuery('');
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add item button */}
                {(role_id == 2 || (activeCategoryId && activeCategory.permission_level !== 'read')) && (
                    <button
                        onClick={() => {
                            if (activeCategory && activeCategoryId) {
                                setSelectedCategoryForModal(activeCategoryId);
                                
                                let decryptedSharedKey = undefined;
                                if (activeCategory.shared_key && typeof activeCategory.shared_key === 'string' && activeCategory.shared_key.length > 0) {
                                    try {
                                        decryptedSharedKey = decryptKeyData(activeCategory.shared_key);
                                    } catch (error) {
                                        console.error('Failed to decrypt shared_key:', error);
                                        decryptedSharedKey = undefined;
                                    }
                                }
                                
                                setSelectedCategoryInfo({
                                    id: activeCategoryId,
                                    isShared: activeCategory.is_shared || false,
                                    sharedKey: decryptedSharedKey,
                                    permissionLevel: activeCategory.permission_level
                                });
                                setIsItemModalOpen(true);
                            }
                        }}
                        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-primary-500/30 flex items-center justify-center z-10 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 active:scale-95"
                        aria-label="Add Item"
                    >
                        <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        <span className="ml-2 hidden sm:inline">Add Item</span>
                    </button>
                )}
            </div>
        );
    };

    return (
        <div className="min-h-screen flex bg-gray-100 overflow-x-hidden">
            <VaultSidebar />
            <div className="flex flex-col flex-1 min-w-0">
                <VaultHeader />

                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 min-h-0 w-full">
                    <div className="w-full">
                        {/* {renderBreadcrumbs()} */}

                        {currentLevel === 'root' && renderRootLevel()}
                        {currentLevel === 'category' && renderCategoryLevel()}
                    </div>
                </main>
            </div>

            {/* Modals - Make sure they're properly positioned */}
            <CategoryModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSubmit={handleCreateCategory}
                title="Create New Vault"
                workspaceId={activeWorkspace?.id || team_id || undefined}
            />

            <CategoryModal
                isOpen={isEditModalOpen}
                onClose={() => {
                    setIsEditModalOpen(false);
                    setSelectedCategory(null);
                }}
                onSubmit={handleUpdateCategory}
                category={selectedCategory || undefined}
                title="Edit Vault"
                workspaceId={activeWorkspace?.id || team_id || undefined}
            />

            <CreateNoteModal
                isOpen={isNoteModalOpen}
                onClose={handleNoteModalClose}
                onSuccess={() => {
                    refreshCurrentCategory();
                    handleNoteModalClose();
                }}
                note={selectedNote || undefined}
            />

            <CardDetailsModal
                isOpen={isCardModalOpen}
                onClose={handleCardModalClose}
                onSuccess={() => {
                    refreshCurrentCategory();
                    handleCardModalClose();
                }}
                card={selectedCard || undefined}
                permissionLevel={selectedCard?.sharedKey ? permissionLevel : ''}
                isSharedCard={selectedCard?.sharedKey ? true : false}
                sharedKey={selectedCard?.sharedKey || undefined}
            />

            <BankDetailsModal
                isOpen={isBankModalOpen}
                onClose={handleBankModalClose}
                onSuccess={() => {
                    refreshCurrentCategory();
                    handleBankModalClose();
                }}
                bank={selectedBank || undefined}
                isSharedBank={selectedBank?.isSharedBank || false}
                permissionLevel={selectedBank?.sharedKey ? permissionLevel : ''}
                sharedKey={selectedBank?.sharedKey || undefined}
            />

            <PersonalDetailsModal
                isOpen={isPersonalModalOpen}
                onClose={handlePersonalModalClose}
                onSuccess={() => {
                    refreshCurrentCategory();
                    handlePersonalModalClose();
                }}
                personal={selectedPersonal || undefined}
                isSharedPersonal={selectedPersonal?.isSharedPersonal || false}
                permissionLevel={selectedPersonal?.sharedKey ? permissionLevel : ''}
                sharedKey={selectedPersonal?.sharedKey || undefined}
            />

            <DeleteCategoryModal
                isOpen={isDeleteModalOpen}
                onClose={() => {
                    setIsDeleteModalOpen(false);
                    setCategoryToDelete(null);
                }}
                categoryId={categoryToDelete?.id || ''}
                categoryName={categoryToDelete?.name || ''}
                onDeleted={() => {
                    getAllCategoriesBasic();
                    setIsDeleteModalOpen(false);
                    setCategoryToDelete(null);
                }}
            />

            <ShareVaultModal
                isOpen={isShareModalOpen}
                onClose={() => {
                    setIsShareModalOpen(false);
                    setVaultToShare(null);
                }}
                onSuccess={handleShareSuccess}
                vaultId={vaultToShare?.id || ''}
                vaultName={vaultToShare?.name || ''}
            />

            {/* New modals for item creation */}
            <AddItemModal
                isOpen={isItemModalOpen}
                onClose={() => {
                    console.log('Closing Add Item modal');
                    setIsItemModalOpen(false);
                }}
                onSelect={handleItemSelect}
            />

            <PasswordModal
                isOpen={isPasswordModalOpen}
                onClose={() => setIsPasswordModalOpen(false)}
                onSuccess={handleSuccess}
            />

            <ConfirmModal
                isOpen={showConfirmModal}
                onClose={() => setShowConfirmModal(false)}
                onConfirm={
                    confirmModalType === 'revoke'
                        ? handleConfirmRevoke
                        : handleConfirmPermissionChange
                }
                title={
                    confirmModalType === 'revoke'
                        ? 'Revoke Access'
                        : 'Change Permission'
                }
                message={
                    confirmModalType === 'revoke'
                        ? `Are you sure you want to revoke access for ${currentSharedEntity.email || currentSharedEntity.name}?`
                        : `Are you sure you want to update the permission level to ${newPermission}?`
                }
                confirmText={
                    confirmModalType === 'revoke'
                        ? 'Revoke'
                        : 'Update'
                }
                isLoading={isProcessing}
            />

            {/* QuickAdd Creation Modals */}
            <PasswordModal
                isOpen={quickAddPasswordModalOpen}
                onClose={() => {
                    setQuickAddPasswordModalOpen(false);
                    setSelectedCategoryForModal(null);
                    setSelectedCategoryInfo(null);
                }}
                onSuccess={handleQuickAddSuccess}
                categoryId={selectedCategoryForModal || ''}
            />

            <CardDetailsModal
                isOpen={quickAddCardModalOpen}
                onClose={() => {
                    setQuickAddCardModalOpen(false);
                    setSelectedCategoryForModal(null);
                    setSelectedCategoryInfo(null);
                }}
                onSuccess={handleQuickAddSuccess}
                categoryId={selectedCategoryForModal || ''}
                isSharedCard={selectedCategoryInfo?.isShared}
                permissionLevel={selectedCategoryInfo?.permissionLevel}
                sharedKey={selectedCategoryInfo?.sharedKey}
            />

            <BankDetailsModal
                isOpen={quickAddBankModalOpen}
                onClose={() => {
                    setQuickAddBankModalOpen(false);
                    setSelectedCategoryForModal(null);
                    setSelectedCategoryInfo(null);
                }}
                onSuccess={handleQuickAddSuccess}
                categoryId={selectedCategoryForModal || ''}
                isSharedBank={selectedCategoryInfo?.isShared}
                permissionLevel={selectedCategoryInfo?.permissionLevel}
                sharedKey={selectedCategoryInfo?.sharedKey}
            />

            <PersonalDetailsModal
                isOpen={quickAddPersonalModalOpen}
                onClose={() => {
                    setQuickAddPersonalModalOpen(false);
                    setSelectedCategoryForModal(null);
                    setSelectedCategoryInfo(null);
                }}
                onSuccess={handleQuickAddSuccess}
                categoryId={selectedCategoryForModal || ''}
                isSharedPersonal={selectedCategoryInfo?.isShared}
                permissionLevel={selectedCategoryInfo?.permissionLevel}
                sharedKey={selectedCategoryInfo?.sharedKey}
            />
        </div>
    );
};

export default CategoriesPage;